import clsx from 'clsx'
import { memo, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Select } from '@wartek-id/select'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import { Dialog, DialogOverlay, DialogContent } from '@wartek-id/dialog-v1'
import { Tooltip } from '@wartek-id/tooltip'

import { PERFORMANCE_PRACTICE_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/performancePractice'
import { PREDICATE_RECOMMENDATION_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/predicateRecommendation'
import { WORK_BEHAVIOR_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/workBehavior'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import { assetUrl } from 'utils/url'
import CardPeriod from 'app/KinerjaShared/components/CardPeriod'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type {
  PredikatKinerjaItem,
  TPerformancePracticeResult,
  TPredicateRecommendationResult,
  TWorkBehaviorResult,
} from 'types/kinerja-pelaksanaan'

type TablePenilaianKinerjaProps = {
  showNilaiAndPoinPengembanganColumns?: boolean
  isLoading?: boolean
  items: Array<PredikatKinerjaItem>
  hasScrollDownButton?: boolean
  onChange?: (items: Array<PredikatKinerjaItem>) => void
  renderAction?: (item: PredikatKinerjaItem, index: number) => React.ReactNode
}

const MAX_PERIOD = 2

export function TablePenilaianKinerja({
  showNilaiAndPoinPengembanganColumns = true,
  items,
  isLoading,
  hasScrollDownButton = true,
  onChange,
  renderAction,
}: TablePenilaianKinerjaProps) {
  const [isDialogFilledBySystemOpen, setDialogFilledBySystemOpen] =
    useState(false)
  const hasActionColumn = typeof renderAction === 'function'

  const handleChangePraktikKinerjaRating = (
    index: number,
    value: TPerformancePracticeResult
  ) => {
    // Clone the original array to be mutated
    const updatedItems = [...items]

    // Update value of Praktik Kinerja rating
    updatedItems[index].performancePractice.result = value

    // Update value of Predikat Kinerja
    const updatedPredikatKinerja = generatePredikatKinerja(
      value,
      updatedItems[index].workBehavior.result
    )
    if (updatedPredikatKinerja) {
      updatedItems[index].predicateRecommendation = updatedPredikatKinerja
    }

    // Sync with parent via callback
    onChange(updatedItems)
  }

  const handleChangePerilakuKerjaRating = (
    index: number,
    value: TWorkBehaviorResult
  ) => {
    // Clone the original array to be mutated
    const updatedItems = [...items]

    // Update value of Praktik Kinerja rating
    updatedItems[index].workBehavior.result = value

    // Update value of Predikat Kinerja
    const updatedPredikatKinerja = generatePredikatKinerja(
      updatedItems[index].performancePractice.result,
      value
    )
    if (updatedPredikatKinerja) {
      updatedItems[index].predicateRecommendation = updatedPredikatKinerja
    }

    // Sync with parent via callback
    onChange(updatedItems)
  }

  const generatePredikatKinerja = (
    praktikKinerjaRating: TPerformancePracticeResult,
    perilakuKerjaRating: TWorkBehaviorResult
  ): TPredicateRecommendationResult | undefined => {
    const dictionary: Record<string, TPredicateRecommendationResult> = {
      'BELOW_EXPECTATION-BELOW_EXPECTATION': 'VERY_BAD',
      'BELOW_EXPECTATION-MEET_EXPECTATION': 'NEED_IMPROVEMENT',
      'BELOW_EXPECTATION-EXCEED_EXPECTATION': 'NEED_IMPROVEMENT',
      'MEET_EXPECTATION-BELOW_EXPECTATION': 'BAD',
      'MEET_EXPECTATION-MEET_EXPECTATION': 'GOOD',
      'MEET_EXPECTATION-EXCEED_EXPECTATION': 'GOOD',
      'EXCEED_EXPECTATION-BELOW_EXPECTATION': 'BAD',
      'EXCEED_EXPECTATION-MEET_EXPECTATION': 'GOOD',
      'EXCEED_EXPECTATION-EXCEED_EXPECTATION': 'VERY_GOOD',
    }

    return dictionary[`${praktikKinerjaRating}-${perilakuKerjaRating}`]
  }

  return (
    <div>
      <table className="w-full bg-white">
        <thead className="bg-blue-90">
          <tr>
            <th
              className="border border-gray-30 font-semibold text-white text-sm p-5"
              rowSpan={2}
            >
              Nama
            </th>
            <th
              className="border border-gray-30 font-semibold text-white text-sm p-5"
              colSpan={showNilaiAndPoinPengembanganColumns ? 2 : 1}
            >
              Rating Praktik Kinerja
            </th>
            <th
              className="border border-gray-30 font-semibold text-white text-sm p-5"
              colSpan={showNilaiAndPoinPengembanganColumns ? 2 : 1}
            >
              Rating Perilaku Kerja
            </th>
            {showNilaiAndPoinPengembanganColumns && (
              <th
                className="border border-gray-30 font-semibold text-white text-sm p-5"
                colSpan={2}
              >
                Poin Pengembangan Kompetensi
              </th>
            )}
            <th
              className="border border-gray-30 font-semibold text-white text-sm p-5"
              rowSpan={2}
            >
              <div className="flex space-x-1 items-center justify-center">
                <span>Predikat Kinerja</span>
                <Tooltip
                  content="Terdiri atas Rating Praktik Kinerja dan Rating Perilaku Kerja."
                  placement="right"
                  strategy="fixed"
                  trigger="hover"
                >
                  <Icon color="inverse">info</Icon>
                </Tooltip>
              </div>
            </th>
            {hasActionColumn && (
              <th
                className="border border-gray-30 font-semibold text-white text-sm px-2 py-5 w-1 whitespace-nowrap"
                rowSpan={2}
              >
                Aksi
              </th>
            )}
          </tr>
          <tr>
            {showNilaiAndPoinPengembanganColumns && (
              <th className="border border-gray-30 font-semibold text-white text-sm p-5">
                <div className="flex space-x-1 items-center justify-center">
                  <span>Nilai</span>
                  <Tooltip
                    content="Penilaian Anda otomatis dikonversi menjadi angka. Kami harap dapat membantu Anda dalam menetapkan rating tiap guru."
                    placement="right"
                    strategy="fixed"
                    trigger="hover"
                  >
                    <Icon color="inverse">info</Icon>
                  </Tooltip>
                </div>
              </th>
            )}
            {showNilaiAndPoinPengembanganColumns && (
              <th className="border border-gray-30 font-semibold text-white text-sm p-5">
                <div className="flex space-x-1 items-center justify-center">
                  <span>Rating</span>
                </div>
              </th>
            )}
            {showNilaiAndPoinPengembanganColumns && (
              <th className="border border-gray-30 font-semibold text-white text-sm p-5">
                <div className="flex space-x-1 items-center justify-center">
                  <span>Nilai</span>
                  <Tooltip
                    content="Penilaian Anda otomatis dikonversi menjadi angka. Kami harap dapat membantu Anda dalam menetapkan rating tiap guru."
                    placement="right"
                    strategy="fixed"
                    trigger="hover"
                  >
                    <Icon color="inverse">info</Icon>
                  </Tooltip>
                </div>
              </th>
            )}
            {showNilaiAndPoinPengembanganColumns && (
              <th className="border border-gray-30 font-semibold text-white text-sm p-5">
                <div className="flex space-x-1 items-center justify-center">
                  <span>Rating</span>
                </div>
              </th>
            )}
            {showNilaiAndPoinPengembanganColumns && (
              <th className="border border-gray-30 font-semibold text-white text-sm p-5">
                Target
              </th>
            )}
            {showNilaiAndPoinPengembanganColumns && (
              <th className="border border-gray-30 font-semibold text-white text-sm p-5">
                Realisasi
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {!isLoading && items.length === 0 && (
            <TableRowStates
              type="EMPTY"
              colSpan={
                showNilaiAndPoinPengembanganColumns
                  ? hasActionColumn
                    ? 9
                    : 8
                  : hasActionColumn
                  ? 5
                  : 4
              }
              children={null}
            />
          )}

          {isLoading ? (
            <TableShimmer
              showNilaiAndPoinPengembanganColumns={
                showNilaiAndPoinPengembanganColumns
              }
              showActionColumn={hasActionColumn}
            />
          ) : (
            items.map((item, i) => (
              <tr key={item.ptkId} className="border border-y-2 border-gray-30">
                <CellText className="w-44 overflow-wrap-anywhere">
                  {item.name}
                </CellText>
                {showNilaiAndPoinPengembanganColumns ? (
                  <>
                    <CellText className="text-center">
                      {item.performancePractice?.score}
                    </CellText>
                    <CellPraktikKinerjaRating
                      isFilledBySystem={item.filledBySystem}
                      value={item.performancePractice?.result}
                      onChange={(e) =>
                        handleChangePraktikKinerjaRating(
                          i,
                          e.target.value as TPerformancePracticeResult
                        )
                      }
                      onClickFilledBySystem={() => {
                        setDialogFilledBySystemOpen(true)
                      }}
                    />
                  </>
                ) : (
                  <CellPraktikKinerjaRating
                    isFilledBySystem={item.filledBySystem}
                    value={item.performancePractice?.result}
                    onChange={(e) =>
                      handleChangePraktikKinerjaRating(
                        i,
                        e.target.value as TPerformancePracticeResult
                      )
                    }
                    onClickFilledBySystem={() => {
                      setDialogFilledBySystemOpen(true)
                    }}
                  />
                )}
                {showNilaiAndPoinPengembanganColumns ? (
                  <>
                    <CellText className="text-center">
                      {item.workBehavior?.score}
                    </CellText>
                    <CellPerilakuKerjaRating
                      value={item.workBehavior?.result}
                      onChange={(e) =>
                        handleChangePerilakuKerjaRating(
                          i,
                          e.target.value as TWorkBehaviorResult
                        )
                      }
                    />
                  </>
                ) : (
                  <CellPerilakuKerjaRating
                    value={item.workBehavior?.result}
                    onChange={(e) =>
                      handleChangePerilakuKerjaRating(
                        i,
                        e.target.value as TWorkBehaviorResult
                      )
                    }
                  />
                )}
                {showNilaiAndPoinPengembanganColumns && (
                  <CellText>
                    {`${item.pointCompetencyImprovement?.target} poin`}
                  </CellText>
                )}
                {showNilaiAndPoinPengembanganColumns && (
                  <CellText>
                    {`${item.pointCompetencyImprovement?.realization} poin`}
                  </CellText>
                )}
                <CellPredikatKinerja value={item.predicateRecommendation} />
                {hasActionColumn && (
                  <CellText className="text-center">
                    <div className="flex items-center justify-center">
                      {renderAction(item, i)}
                    </div>
                  </CellText>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {!isLoading && hasScrollDownButton && <ButtonScrollDown />}

      <DialogFilledBySystem
        isOpen={isDialogFilledBySystemOpen}
        onClose={() => setDialogFilledBySystemOpen(false)}
      />
    </div>
  )
}

type CellPraktikKinerjaRatingProps = {
  isFilledBySystem?: boolean
  value?: TPerformancePracticeResult
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void
  onClickFilledBySystem?: () => void
}
function CellPraktikKinerjaRating({
  isFilledBySystem,
  value,
  onChange,
  onClickFilledBySystem,
}: CellPraktikKinerjaRatingProps) {
  const bgColor = !!value ? 'bg-blue-10' : 'bg-white'

  return (
    <td className={clsx('border border-y border-gray-30 p-2 w-48', bgColor)}>
      {isFilledBySystem ? (
        <div className="flex items-center gap-2">
          <Text as="span" variant="body-sm">
            {PERFORMANCE_PRACTICE_RESULT_TEXT[value]}
          </Text>
          <Button
            iconOnly
            size="sm"
            className="!bg-transparent"
            aria-label="Lihat Info"
            onClick={onClickFilledBySystem}
          >
            <Icon>help</Icon>
          </Button>
        </div>
      ) : (
        <Select
          className="whitespace-nowrap"
          value={value}
          onChange={onChange}
          isDisabled={isFilledBySystem}
        >
          <option value="" disabled selected>
            Pilih Rating
          </option>
          {Object.entries(PERFORMANCE_PRACTICE_RESULT_TEXT).map(
            ([performancePractice, label]) => (
              <option value={performancePractice} key={performancePractice}>
                {label}
              </option>
            )
          )}
        </Select>
      )}
    </td>
  )
}

type CellPerilakuKerjaRatingProps = {
  value?: TPerformancePracticeResult
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void
}
function CellPerilakuKerjaRating({
  value,
  onChange,
}: CellPerilakuKerjaRatingProps) {
  const bgColor = !!value ? 'bg-blue-10' : 'bg-white'

  return (
    <td className={clsx('border border-y border-gray-30 p-2 w-48', bgColor)}>
      <Select className="whitespace-nowrap" value={value} onChange={onChange}>
        <option value="" disabled selected>
          Pilih Rating
        </option>
        {Object.entries(WORK_BEHAVIOR_RESULT_TEXT).map(
          ([workBehavior, label]) => (
            <option value={workBehavior} key={workBehavior}>
              {label}
            </option>
          )
        )}
      </Select>
    </td>
  )
}

type CellPredikatKinerjaProps = {
  value?: TPredicateRecommendationResult
}
const CellPredikatKinerja = memo(function CellPredikatKinerja({
  value,
}: CellPredikatKinerjaProps) {
  const bgColor = !value
    ? 'bg-white'
    : value === 'GOOD' || value === 'VERY_GOOD'
    ? 'bg-blue-10'
    : 'bg-red-20'

  return (
    <Text
      as="td"
      className={clsx('border border-y border-gray-30 px-4 py-5 w-36', bgColor)}
      variant="body-sm"
      color={
        PREDICATE_RECOMMENDATION_RESULT_TEXT[value] ? 'default' : 'subdued'
      }
    >
      {PREDICATE_RECOMMENDATION_RESULT_TEXT[value] ??
        'Pilih Rating terlebih dahulu'}
    </Text>
  )
})

type CellTextProps = {
  children?: React.ReactNode
  className?: string
}
const CellText = memo(function CellText({
  children,
  className,
}: CellTextProps) {
  return (
    <Text
      as="td"
      variant="body-sm"
      className={clsx('border-y border border-gray-30 px-4 py-5', className)}
    >
      {children}
    </Text>
  )
})

type TableShimmerProps = {
  showNilaiAndPoinPengembanganColumns?: boolean
  showActionColumn?: boolean
}

function TableShimmer({
  showNilaiAndPoinPengembanganColumns = true,
  showActionColumn = false,
}: TableShimmerProps) {
  const rows = 6
  const cols = showNilaiAndPoinPengembanganColumns
    ? showActionColumn
      ? 9
      : 8
    : showActionColumn
    ? 5
    : 4

  return (
    <>
      {Array(rows)
        .fill('')
        .map((_, i) => (
          <tr key={i}>
            {Array(cols)
              .fill('')
              .map((_, j) => (
                <td
                  className="text-center border border-y border-gray-30 p-2"
                  key={`${i}-${j}`}
                >
                  <Shimmer height="40px" width="100%" />
                </td>
              ))}
          </tr>
        ))}
    </>
  )
}

function ButtonScrollDown() {
  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0,
  })

  return (
    <>
      {/* Sentinel element to detect inView */}
      <div className="w-full bg-red-70" ref={ref}></div>

      {!inView && (
        <Button
          className="!rounded-full space-x-1 sticky bottom-4 mx-auto mt-4 z-10"
          style={{
            boxShadow: 'var(--elevation-04)',
          }}
          onClick={() => {
            window.scrollTo({
              left: 0,
              top: document.body.scrollHeight,
              behavior: 'smooth',
            })
          }}
        >
          <Icon color="inverse">arrow_downward</Icon>
          <span>Cek lebih lanjut</span>
        </Button>
      )}
    </>
  )
}

type DialogFilledBySystemProps = Omit<DialogProps, 'size' | 'children'> & {
  isGuru?: boolean
}

function DialogFilledBySystem(props: DialogFilledBySystemProps) {
  const { periodYear: currentPeriodYear, periodNo: currentPeriodNumber } =
    usePeriodContext()
  const nextPeriodNumber =
    currentPeriodNumber === MAX_PERIOD ? 1 : currentPeriodNumber + 1
  const nextPeriodYear =
    currentPeriodNumber === MAX_PERIOD
      ? currentPeriodYear + 1
      : currentPeriodYear

  return (
    <Dialog {...props} size="sm">
      <DialogOverlay />
      <DialogContent aria-labelledby="card-block-period-dialog-title">
        <div className="p-6 space-y-8">
          <div className="space-y-3">
            <img
              src={assetUrl('/illustrations/img-empty-document-2.png')}
              alt=""
              width={84}
              height={84}
              className="mb-4"
            />

            <Text variant="display-sm" as="h2">
              Dinilai secara otomatis oleh sistem
            </Text>

            <Text variant="body-lg" color="subdued">
              Ini terjadi karena Guru ini baru memperoleh Penugasan JF Guru.
              Penyesuaian hanya untuk periode ini.
            </Text>
          </div>

          <aside className="p-4 rounded bg-orange-10 flex space-x-2">
            <Icon
              fontSize="sm"
              className="!text-xl !leading-5 !text-yellow-700"
            >
              info
            </Icon>
            <Text variant="body-sm">
              Mulai periode berikutnya (
              <CardPeriod
                period={nextPeriodNumber}
                year={nextPeriodYear}
                separator="-"
              />
              ) dan seterusnya, Anda diharapkan melakukan Penilaian Pengelolaan
              Kinerja sesuai jadwal seharusnya.
            </Text>
          </aside>

          <Button
            color="blue"
            size="lg"
            type="button"
            onClick={() => props.onClose?.()}
          >
            Saya mengerti
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
